import { observable, action, runInAction, computed } from "mobx";
import isEqual from "lodash/isEqual";
import clone from "lodash/clone";

import PageStore from "app/PageStore";

import RfeService from "./RfeService";
import { EntityType, Correction, BlockInfo, EntityTypedText } from "./models";
import BlockForm from "./commands/BlockForm";

export default class BlockStore {
  @observable public loading = false;
  @observable public entities: EntityType[];
  @observable public corrections: Correction[];

  @observable public form: BlockForm;

  @observable private selectedId?: number;
  @observable private blockInfo?: BlockInfo;

  private pageStore: PageStore;
  private rfeService: RfeService;

  constructor(pageStore: PageStore, rfeService: RfeService) {
    this.pageStore = pageStore;
    this.rfeService = rfeService;
    this.reset();
  }

  @computed
  get allEntities() {
    return this.entities.concat(this.form.entities);
  }

  @computed
  get allCorrections() {
    return this.corrections.concat(this.form.corrections);
  }

  @computed
  get block() {
    return this.selectedId ? this.blockInfo : undefined;
  }

  @computed
  get words() {
    if (!this.block || !this.block.text) {
      return [];
    }
    const words = clone(this.block.text);
    const convertedWords: Array<string | EntityTypedText> = [];
    words.forEach((word: string, index: number) => {
      if (word.length === 0) {
        return;
      }
      const editedEntity = this.allEntities.find((e) => {
        const wordIndex = e.entity.indexOf(word);
        if (wordIndex === 0) {
          const splited = e.entity.split(/ | /gi);
          const entityArray = words.slice(index, index + splited.length);
          if (isEqual(splited, entityArray)) {
            words.splice(index, splited.length - 1);
            convertedWords.push({
              type: e.entityType,
              words: splited,
            } as EntityTypedText);
            return true;
          }
        }
        return false;
      });
      if (!editedEntity) {
        convertedWords.push(word);
      }
    });
    return convertedWords;
  }

  @computed
  get result() {
    return this.block ? this.blockInfo!.result : undefined;
  }

  @action.bound
  public async loadBlock(blockId: number) {
    const { openSpinner, openToast, closeSpinner } = this.pageStore;
    this.loading = true;
    openSpinner();
    try {
      if (!blockId) {
        throw new Error("Not valid block id");
      }
      const res = await this.rfeService.getBlock(blockId);
      if (!res) {
        throw res;
      }
      const corrections = await this.rfeService.findCorrections(blockId);
      if (!corrections) {
        throw corrections;
      }
      const entities = await this.rfeService.findEntities(blockId);
      if (!entities) {
        throw entities;
      }
      runInAction(() => {
        const { categoryAuto, categoryManual, feedback } = res;
        const category = categoryManual ? categoryManual : categoryAuto;
        this.selectedId = blockId;
        this.blockInfo = new BlockInfo(blockId, res);
        this.corrections = corrections;
        this.entities = entities;
        this.form.update({
          category,
          feedback,
        });
        this.form.initialize();
      });
    } catch (e) {
      openToast(e.message, { type: "error" });
    } finally {
      this.loading = false;
      closeSpinner();
    }
  }

  @action.bound
  public async submit() {
    const { openSpinner, openToast, closeSpinner } = this.pageStore;
    openSpinner();
    this.loading = true;
    try {
      const res = await this.rfeService.updateBlock(
        this.selectedId!,
        this.form,
      );
      if (!res) {
        throw res;
      }
      runInAction(() => {
        this.corrections = this.corrections.concat(this.form.corrections);
        this.entities = this.entities.concat(this.form.entities);
        this.form.update({
          corrections: [],
          entities: [],
        });
        this.form.initialize();
      });
    } catch (e) {
      openToast(e.message, { type: "error" });
    } finally {
      closeSpinner();
      this.loading = false;
    }
  }

  @action.bound
  public reset() {
    this.loading = false;
    this.entities = [];
    this.corrections = [];
    this.form = new BlockForm();
    this.selectedId = undefined;
    this.blockInfo = undefined;
  }
}
